import React, { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Post, v1Patch, v1Upload, v1Delete } from '@/lib/api';
import { useDerivedSelection } from '@/hooks/use-derived-state';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Save, ImageIcon, Check, Upload, Trash2, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import QuestionBankBulkUpload from '@/components/QuestionBankBulkUpload';
import QuestionBankReadOnlyBanner from '@/components/QuestionBankReadOnlyBanner';
import UnsavedRefreshGuard from '@/components/UnsavedRefreshGuard';
import { Gated } from '@/auth/permissions';
import { bankWriteErrorMessage, useBankEditGuard } from '@/hooks/useBankEditGuard';
import {
  QuestionBankBulkDeleteBar,
  QuestionEditorDeleteButton,
  QuestionRowCheckbox,
  useCheckedQuestionIds,
} from '@/components/QuestionBankDeleteControls';

const EDIT_PERM = 'bank.psa.edit';
const API_PATH = '/questions/mcq';
const QUERY_KEY = ['v1-mcq-qs'];

import { IQ_CATEGORIES } from '@/lib/reportConstants';

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const STATUSES = ['draft', 'active', 'retired'];
const DEFAULT_IQ_TOPIC = IQ_CATEGORIES[0];

const BLANK_FORM = {
  text: '',
  options: ['', '', '', ''],
  correct: 0,
  difficulty: 'medium',
  topic: DEFAULT_IQ_TOPIC,
  tags: '',
};

function isNewFormDirty(form) {
  return form.text.trim() !== ''
    || form.options.some((o) => o.trim() !== '')
    || form.correct !== BLANK_FORM.correct
    || form.difficulty !== BLANK_FORM.difficulty
    || form.topic !== BLANK_FORM.topic
    || form.tags.trim() !== '';
}

function parseTagNames(text) {
  return text.split(',').map((t) => t.trim()).filter(Boolean);
}

function sameStringList(a, b) {
  const aa = [...a].sort();
  const bb = [...b].sort();
  return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
}

function isMcqEditorDirty(q, { text, opts, correct, difficulty, topic, status, tagsText }) {
  const savedTopic = q.topic || DEFAULT_IQ_TOPIC;
  const localTopic = topic || DEFAULT_IQ_TOPIC;
  const savedTags = (q.tags || []).map((t) => t.name);
  return text !== q.text
    || correct !== q.correct_index
    || difficulty !== q.difficulty
    || localTopic !== savedTopic
    || status !== q.status
    || !sameStringList(savedTags, parseTagNames(tagsText))
    || opts.length !== q.options.length
    || opts.some((o, i) => o !== q.options[i]);
}

export default function McqBank() {
  const qc = useQueryClient();
  const { readOnly, denyEdit, guard } = useBankEditGuard(EDIT_PERM);
  const { data: qs } = useQuery({ queryKey: QUERY_KEY, queryFn: () => v1Get(API_PATH) });
  const [selected, setSelected] = useDerivedSelection(qs);
  const [newOpen, setNewOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const { checked, toggle, clear, setAll } = useCheckedQuestionIds();
  if (!qs) return null;

  const resetForm = () => setForm({ ...BLANK_FORM });

  const handleNewDialogOpenChange = (open) => {
    if (!open) {
      if (isNewFormDirty(form)) {
        setNewOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetForm();
    }
    setNewOpen(open);
  };

  const handleDiscardConfirmOpenChange = (open) => {
    setDiscardConfirmOpen(open);
    if (!open) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isNewFormDirty(form)) {
        setNewOpen(true);
      }
    }
  };

  const confirmDiscardNewForm = () => {
    discardConfirmedRef.current = true;
    resetForm();
    setNewOpen(false);
  };

  const afterDelete = (ids) => {
    const gone = new Set(Array.isArray(ids) ? ids : [ids]);
    clear();
    qc.invalidateQueries({ queryKey: QUERY_KEY });
    if (selected && gone.has(selected.id)) setSelected(null);
  };

  const create = async () => {
    if (readOnly) return denyEdit();
    if (!form.text.trim()) return toast.error('Question text required');
    if (form.options.some((o) => !o.trim())) return toast.error('All four options must be filled');
    if (!form.topic) return toast.error('IQ category is required');
    try {
      const created = await v1Post(API_PATH, {
        text: form.text,
        options: form.options,
        correct_index: form.correct,
        difficulty: form.difficulty,
        topic: form.topic,
        status: 'draft',
        tag_names: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      setSelected(created);
      setNewOpen(false);
      resetForm();
      toast.success('IQ Assessment draft created');
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Failed to create question'));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="IQ Assessment Question Bank"
        subtitle="Problem-solving assessment — single-correct multiple-choice questions with optional images. Auto-scored on submission."
        actions={(
          <Gated need={EDIT_PERM}>
            <div className="flex gap-2">
              <Button variant="outline" className="gap-1.5" onClick={() => setBulkOpen(true)} data-testid="psa-bulk-upload-button">
                <FileUp className="h-4 w-4" /> Bulk upload
              </Button>
              <Button className="gap-1.5" onClick={() => setNewOpen(true)} data-testid="new-mcq-q-button"><Plus className="h-4 w-4" /> New question</Button>
            </div>
          </Gated>
        )}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden"><CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-3" data-testid="question-list">
          <p className="shrink-0 px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Questions ({qs.length})</p>
          <div className="shrink-0">
            <QuestionBankBulkDeleteBar
              ids={qs.map((q) => q.id)}
              checked={checked}
              toggle={toggle}
              setAll={setAll}
              clear={clear}
              apiPath={API_PATH}
              editPermission={EDIT_PERM}
              onDeleted={afterDelete}
              testIdPrefix="psa-bank"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {qs.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs text-muted-foreground">No questions yet — click <span className="font-medium">New question</span> to add the first one.</p>
            ) : (
              <ul className="space-y-1">
                {qs.map((q) => (
                  <li key={q.id} className="flex items-start gap-2">
                    <QuestionRowCheckbox
                      id={q.id}
                      checked={checked.has(q.id)}
                      onToggle={toggle}
                      editPermission={EDIT_PERM}
                      testId={`mcq-check-${q.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => setSelected(q)}
                      className={`flex min-w-0 flex-1 flex-col items-start gap-1 rounded-lg px-3 py-2 text-left text-sm transition-colors ${selected?.id === q.id ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
                      data-testid={`mcq-list-item-${q.id}`}
                    >
                      <span className="line-clamp-2">{q.text}</span>
                      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground capitalize">
                        {q.difficulty} · {q.topic || '—'}{q.image_url && <ImageIcon className="h-3 w-3" />}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent></Card>
        {selected && (
          <McqEditor
            q={selected}
            key={selected.id}
            readOnly={readOnly}
            guard={guard}
            onChange={() => qc.invalidateQueries({ queryKey: QUERY_KEY })}
            onDeleted={() => afterDelete(selected.id)}
          />
        )}
      </div>

      <Dialog open={newOpen} onOpenChange={handleNewDialogOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-mcq-q-dialog">
          <DialogHeader>
            <DialogTitle>New IQ Assessment question</DialogTitle>
            <DialogDescription>Add a 4-option, single-correct question. You can attach an image after creating.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Question text</label>
              <Textarea rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="What is the time complexity of inserting into a balanced BST?" data-testid="mcq-new-text" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground">Options (click circle to mark correct)</label>
              {form.options.map((o, i) => (
                <div key={i} className={`flex items-center gap-2 rounded-lg border p-2 ${i === form.correct ? 'border-emerald-300 bg-emerald-50' : 'bg-card'}`}>
                  <button type="button" onClick={() => setForm({ ...form, correct: i })} className={`grid h-6 w-6 place-items-center rounded-full border text-[11px] font-semibold ${i === form.correct ? 'border-emerald-500 bg-emerald-500 text-white' : 'text-muted-foreground'}`}>{i === form.correct ? <Check className="h-3.5 w-3.5" /> : String.fromCharCode(65 + i)}</button>
                  <Input value={o} onChange={(e) => { const n = [...form.options]; n[i] = e.target.value; setForm({ ...form, options: n }); }} placeholder={`Option ${String.fromCharCode(65 + i)}`} data-testid={`mcq-new-option-${i}`} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Difficulty</label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger className="capitalize"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIFFICULTIES.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">IQ Category</label>
                <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                  <SelectTrigger data-testid="mcq-new-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {IQ_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Tags (comma-separated)</label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="SE_Dev, Security" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => handleNewDialogOpenChange(false)}>Cancel</Button>
            <Button onClick={create} data-testid="new-mcq-q-confirm">Create question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid="new-mcq-q-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered question data will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="new-mcq-q-continue-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardNewForm} data-testid="new-mcq-q-discard-confirm">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnsavedRefreshGuard
        armed={newOpen && isNewFormDirty(form)}
        testIdPrefix="new-mcq-q"
      />

      <QuestionBankBulkUpload
        kind="psa"
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={() => qc.invalidateQueries({ queryKey: ['v1-mcq-qs'] })}
      />
    </div>
  );
}

function McqEditor({ q, readOnly, guard, onChange, onDeleted }) {
  const [text, setText] = useState(q.text);
  const [opts, setOpts] = useState(q.options);
  const [correct, setCorrect] = useState(q.correct_index);
  const [difficulty, setDifficulty] = useState(q.difficulty);
  const [topic, setTopic] = useState(q.topic || DEFAULT_IQ_TOPIC);
  const [status, setStatus] = useState(q.status);
  const [tagsText, setTagsText] = useState((q.tags || []).map((t) => t.name).join(', '));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const dirty = useMemo(
    () => isMcqEditorDirty(q, { text, opts, correct, difficulty, topic, status, tagsText }),
    [q, text, opts, correct, difficulty, topic, status, tagsText],
  );

  const save = guard(async () => {
    if (saving || !dirty) return;
    if (opts.some((o) => !o.trim())) return toast.error('All four options must be filled');
    setSaving(true);
    try {
      const tagList = tagsText.split(',').map((t) => t.trim()).filter(Boolean);
      await v1Patch(`${API_PATH}/${q.id}`, {
        text, options: opts, correct_index: correct, difficulty, topic: topic || null, status,
        tag_names: tagList,
      });
      toast.success('Question saved');
      onChange?.();
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  });

  const onPickFile = guard(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so re-selecting same file fires change
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be ≤ 5 MB');
    setUploading(true);
    try {
      await v1Upload(`/questions/mcq/${q.id}/image`, file);
      toast.success('Image uploaded');
      onChange?.();
    } catch (err) {
      toast.error(bankWriteErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  });

  const clearImage = guard(async () => {
    try {
      await v1Delete(`/questions/mcq/${q.id}/image`);
      toast.success('Image cleared');
      onChange?.();
    } catch (e) {
      toast.error(bankWriteErrorMessage(e, 'Failed to clear image'));
    }
  });

  return (
    <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden"><CardContent className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6" data-testid="question-editor-form">
      {readOnly && <QuestionBankReadOnlyBanner testId="mcq-editor-read-only" />}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Editing IQ Assessment</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus} disabled={readOnly}>
            <SelectTrigger className="w-32" data-testid="mcq-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Question text</label>
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} disabled={readOnly} data-testid="mcq-editor-text" />
      </div>

      <div className="rounded-xl border border-dashed bg-secondary/40 p-4 text-center" data-testid="mcq-image-area">
        {q.image_url ? (
          <div className="space-y-3">
            <img src={q.image_url} alt="Question" className="mx-auto max-h-48 rounded-md object-contain" />
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || readOnly} className="gap-1.5" data-testid="mcq-image-replace">
                <Upload className="h-3.5 w-3.5" /> {uploading ? 'Uploading…' : 'Replace image'}
              </Button>
              <Button size="sm" variant="ghost" onClick={clearImage} disabled={readOnly} className="gap-1.5 text-rose-600 hover:text-rose-700" data-testid="mcq-image-clear">
                <Trash2 className="h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          </div>
        ) : (
          <>
            <ImageIcon className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-xs text-muted-foreground">PNG, JPG or WebP, up to 5 MB</p>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || readOnly} className="mt-3 gap-1.5" data-testid="mcq-image-upload">
              <Upload className="h-3.5 w-3.5" /> {uploading ? 'Uploading…' : 'Upload image'}
            </Button>
          </>
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPickFile} data-testid="mcq-image-file" />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-muted-foreground">Options (mark correct)</label>
        {opts.map((o, i) => (
          <div key={i} className={`flex items-center gap-2 rounded-lg border p-2 ${i === correct ? 'border-emerald-300 bg-emerald-50' : 'bg-card'}`}>
            {/* Left clickable while read-only so the refusal can be reported;
                a disabled button would swallow the click silently. */}
            <button type="button" onClick={guard(() => setCorrect(i))} className={`grid h-6 w-6 place-items-center rounded-full border text-[11px] font-semibold ${i === correct ? 'border-emerald-500 bg-emerald-500 text-white' : 'text-muted-foreground'} ${readOnly ? 'cursor-not-allowed' : ''}`} data-testid={`mcq-editor-correct-${i}`}>{i === correct ? <Check className="h-3.5 w-3.5" /> : String.fromCharCode(65 + i)}</button>
            <Input value={o} onChange={(e) => { const n = [...opts]; n[i] = e.target.value; setOpts(n); }} disabled={readOnly} data-testid={`mcq-editor-option-${i}`} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Difficulty</label>
          <Select value={difficulty} onValueChange={setDifficulty} disabled={readOnly}>
            <SelectTrigger className="capitalize"><SelectValue /></SelectTrigger>
            <SelectContent>{DIFFICULTIES.map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">IQ Category</label>
          <Select value={topic || DEFAULT_IQ_TOPIC} onValueChange={setTopic} disabled={readOnly}>
            <SelectTrigger data-testid="mcq-editor-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {IQ_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><label className="text-xs font-medium text-muted-foreground">Tags (comma-separated)</label><Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} disabled={readOnly} data-testid="mcq-editor-tags" /></div>
      </div>
      {q.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {q.tags.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-[11px]">{t.name}</span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <QuestionEditorDeleteButton
          apiPath={API_PATH}
          questionId={q.id}
          questionPreview={text}
          editPermission={EDIT_PERM}
          onDeleted={onDeleted}
          testId="mcq-editor-delete"
        />
        <Button onClick={save} disabled={saving || readOnly || !dirty} className="gap-1.5" data-testid="mcq-editor-save"><Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save question'}</Button>
      </div>
    </CardContent></Card>
  );
}
